import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Shield, FileText, Send } from 'lucide-react';
import { API } from '@/config/api';

export default function AccessRequest() {
  const { udtId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [documentInfo, setDocumentInfo] = useState(null);
  const [formData, setFormData] = useState({
    requester_name: '',
    requester_email: '',
    purpose: ''
  });
  
  const purposeOptions = [
    'KYC Verification',
    'Bank Loan Application',
    'Home Loan Processing',
    'Personal Loan',
    'Vehicle Loan',
    'Education Loan',
    'Business Loan / MSME Loan',
    'Credit Card Application',
    'Insurance Claim',
    'Legal Proceedings / Court Case',
    'Employment Verification',
    'Background Verification',
    'Government Tender Submission',
    'Government Scheme Application',
    'Educational Admission',
    'Scholarship Application',
    'Visa Application',
    'Passport Application',
    'Company Registration / MCA Filing',
    'GST Registration',
    'Import/Export License',
    'Property Registration',
    'Property Verification',
    'Tenant Verification',
    'Medical Insurance',
    'Medical Claim',
    'Audit Purpose',
    'Tax Filing / ITR',
    'Compliance Check',
    'Due Diligence',
    'Business Transaction',
    'Vendor Onboarding',
    'Contract Agreement',
    'Personal Records',
    'Family Requirements',
    'Other (Specify in Details)'
  ];

  const handleSubmit = async () => {
    if (!formData.requester_name || !formData.requester_email || !formData.purpose) {
      toast.error('Please fill all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/access/request`, {
        udt_id: udtId,
        ...formData
      });
      toast.success('Access request sent successfully!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-3 sm:p-4 min-w-0" style={{ background: 'linear-gradient(135deg, #E8F2FF 0%, #F5F9FF 100%)' }}>
      <div className="w-full max-w-2xl min-w-0">
        <div className="text-center mb-6 sm:mb-8">
          <Shield className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 flex-shrink-0" style={{ color: '#0F52BA' }} />
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 px-1" style={{ color: '#0F52BA', fontFamily: 'Work Sans' }}>Document Access Request</h1>
          <p className="text-sm text-gray-600">Request secure access to document</p>
        </div>

        <Card className="min-w-0">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl">Request Access</CardTitle>
            <CardDescription className="break-all">
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <FileText className="w-4 h-4 flex-shrink-0" />
                <span>UDT ID: {udtId}</span>
              </div>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 p-4 sm:p-6 pt-0">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm">Your Name</Label>
              <Input
                id="name"
                data-testid="requester-name-input"
                placeholder="John Doe"
                value={formData.requester_name}
                onChange={(e) => setFormData({ ...formData, requester_name: e.target.value })}
                className="w-full min-w-0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm">Your Email</Label>
              <Input
                id="email"
                data-testid="requester-email-input"
                type="email"
                placeholder="john@example.com"
                value={formData.requester_email}
                onChange={(e) => setFormData({ ...formData, requester_email: e.target.value })}
                className="w-full min-w-0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="purpose" className="text-sm">Purpose of Access *</Label>
              <select
                id="purpose"
                data-testid="purpose-select"
                className="w-full min-w-0 px-3 py-2 border rounded-md text-sm"
                value={formData.purpose}
                onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              >
                <option value="">Select Purpose</option>
                {purposeOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="additional-details" className="text-sm">Additional Details (Optional)</Label>
              <Textarea
                id="additional-details"
                data-testid="additional-details-textarea"
                placeholder="Any additional information..."
                rows={3}
                className="w-full min-w-0"
              />
            </div>
          </CardContent>
          <CardFooter className="p-4 sm:p-6 pt-0">
            <Button
              data-testid="submit-request-button"
              onClick={handleSubmit}
              disabled={loading}
              className="w-full"
              style={{ background: '#0F52BA' }}
            >
              <Send className="w-4 h-4 mr-2" />
              {loading ? 'Sending...' : 'Send Request'}
            </Button>
          </CardFooter>
        </Card>

        <div className="mt-6 text-center">
          <p className="text-sm" style={{ color: '#666' }}>
            The document owner will review your request and may approve or reject it.
          </p>
        </div>
      </div>
    </div>
  );
}
